import React, { useState } from 'react';
import { X, User, Car, Wrench, Truck, AlertTriangle, Check, Loader2 } from 'lucide-react';

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</span>
    {children}
  </label>
);

const inputClass =
  'w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400';

const SectionStatus = ({ error, success }) => {
  if (error) {
    return (
      <p className="text-[11px] text-red-600 flex items-center gap-1">
        <AlertTriangle size={11} className="shrink-0" />
        {error}
      </p>
    );
  }
  if (success) {
    return (
      <p className="text-[11px] text-green-600 flex items-center gap-1">
        <Check size={11} className="shrink-0" />
        {success}
      </p>
    );
  }
  return null;
};

const SaveButton = ({ onClick, disabled, saving, label = 'Save' }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || saving}
    className="self-start flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
  >
    {saving && <Loader2 size={12} className="animate-spin" />}
    {saving ? 'Saving...' : label}
  </button>
);

const EditLeadDetailsModal = ({ lead, onClose, onSubmit }) => {
  const appointment = lead.appointment || {};

  const splitName = (lead.customerName && lead.customerName !== 'Customer Pending'
    ? lead.customerName
    : ''
  ).trim().split(/\s+/).filter(Boolean);

  // --- Customer section ---
  const [customerFirstName, setCustomerFirstName] = useState(splitName[0] || '');
  const [customerLastName, setCustomerLastName] = useState(splitName.slice(1).join(' ') || '');
  const [customerSaving, setCustomerSaving] = useState(false);
  const [customerError, setCustomerError] = useState(null);
  const [customerSuccess, setCustomerSuccess] = useState(null);
  // Tracks a customer created/attached during THIS modal session, so the vehicle section unlocks
  // right away without waiting for the parent list to refresh and hand back a new `lead` prop.
  const [customerJustSaved, setCustomerJustSaved] = useState(false);

  const hasExistingCustomer = Boolean(lead.customerName) && lead.customerName !== 'Customer Pending';
  const customerExists = hasExistingCustomer || customerJustSaved;
  const hasCustomerInput = Boolean(customerFirstName.trim() || customerLastName.trim());

  // --- Vehicle section ---
  const [vehicleMake, setVehicleMake] = useState(lead.vehicle?.make || '');
  const [vehicleModel, setVehicleModel] = useState(lead.vehicle?.model || '');
  const [vehicleYear, setVehicleYear] = useState(lead.vehicle?.year ? String(lead.vehicle.year) : '');
  const [vehicleVin, setVehicleVin] = useState(lead.vehicle?.vin || '');
  const [vehicleSaving, setVehicleSaving] = useState(false);
  const [vehicleError, setVehicleError] = useState(null);
  const [vehicleSuccess, setVehicleSuccess] = useState(null);

  const hasVehicleInput = Boolean(
    vehicleMake.trim() || vehicleModel.trim() || vehicleYear.trim() || vehicleVin.trim()
  );

  // --- Service section ---
  const [service, setService] = useState(appointment.service || '');
  const [opcode, setOpcode] = useState(appointment.opcode || '');
  const [serviceSaving, setServiceSaving] = useState(false);
  const [serviceError, setServiceError] = useState(null);
  const [serviceSuccess, setServiceSuccess] = useState(null);

  const hasServiceInput = Boolean(service.trim() || opcode.trim());

  // --- Transportation section ---
  const [transportationType, setTransportationType] = useState(appointment.transportationType || '');
  const [transportSaving, setTransportSaving] = useState(false);
  const [transportError, setTransportError] = useState(null);
  const [transportSuccess, setTransportSuccess] = useState(null);

  const emptyPayload = {
    customerFirstName: null,
    customerLastName: null,
    vehicleMake: null,
    vehicleModel: null,
    vehicleYear: null,
    vehicleVin: null,
    service: null,
    opcode: null,
    transportationType: null,
  };

  const saveSection = async (fields, { setSaving, setError, setSuccess }) => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await onSubmit(lead.id, { ...emptyPayload, ...fields });
      if (result?.ok) {
        setSuccess(result.message || 'Saved.');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result?.message || 'Failed to save.');
      }
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to save.');
      return { ok: false };
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCustomer = async () => {
    const result = await saveSection(
      {
        customerFirstName: customerFirstName.trim() || null,
        customerLastName: customerLastName.trim() || null,
      },
      { setSaving: setCustomerSaving, setError: setCustomerError, setSuccess: setCustomerSuccess }
    );
    if (result?.ok) {
      setCustomerJustSaved(true);
    }
  };

  const handleSaveVehicle = () => {
    saveSection(
      {
        vehicleMake: vehicleMake.trim() || null,
        vehicleModel: vehicleModel.trim() || null,
        vehicleYear: vehicleYear.trim() ? Number(vehicleYear.trim()) : null,
        vehicleVin: vehicleVin.trim() || null,
      },
      { setSaving: setVehicleSaving, setError: setVehicleError, setSuccess: setVehicleSuccess }
    );
  };

  const handleSaveService = () => {
    saveSection(
      { service: service.trim() || null, opcode: opcode.trim() || null },
      { setSaving: setServiceSaving, setError: setServiceError, setSuccess: setServiceSuccess }
    );
  };

  const handleSaveTransport = () => {
    saveSection(
      { transportationType: transportationType.trim() || null },
      { setSaving: setTransportSaving, setError: setTransportError, setSuccess: setTransportSuccess }
    );
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-bold text-gray-900">Edit lead details</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Fill in or correct customer, vehicle, service and transportation info by hand -
              useful when N2 missed something or is off. Each section saves on its own.
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100"
            title="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-5">
          {/* Customer */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-700">
              <User size={13} className="text-gray-400" />
              Customer
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="First name">
                <input
                  type="text"
                  value={customerFirstName}
                  onChange={(e) => setCustomerFirstName(e.target.value)}
                  className={inputClass}
                  placeholder="First"
                />
              </Field>
              <Field label="Last name">
                <input
                  type="text"
                  value={customerLastName}
                  onChange={(e) => setCustomerLastName(e.target.value)}
                  className={inputClass}
                  placeholder="Last"
                />
              </Field>
            </div>
            {lead.customerPhone && (
              <p className="text-[11px] text-gray-400">Phone (from the conversation): {lead.customerPhone}</p>
            )}
            <SaveButton onClick={handleSaveCustomer} disabled={!hasCustomerInput} saving={customerSaving} />
            <SectionStatus error={customerError} success={customerSuccess} />
          </div>

          <div className="border-t border-gray-100" />

          {/* Vehicle */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-700">
              <Car size={13} className="text-gray-400" />
              Vehicle
            </div>
            {!customerExists && (
              <p className="text-[11px] text-amber-600 flex items-center gap-1">
                <AlertTriangle size={11} className="shrink-0" />
                Save the customer above first - a vehicle needs a customer to attach to.
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Make">
                <input
                  type="text"
                  value={vehicleMake}
                  onChange={(e) => setVehicleMake(e.target.value)}
                  className={inputClass}
                  placeholder="e.g. Mazda"
                  disabled={!customerExists}
                />
              </Field>
              <Field label="Model">
                <input
                  type="text"
                  value={vehicleModel}
                  onChange={(e) => setVehicleModel(e.target.value)}
                  className={inputClass}
                  placeholder="e.g. CX-30"
                  disabled={!customerExists}
                />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Year">
                <input
                  type="number"
                  value={vehicleYear}
                  onChange={(e) => setVehicleYear(e.target.value)}
                  className={inputClass}
                  placeholder="e.g. 2026"
                  disabled={!customerExists}
                />
              </Field>
              <Field label="VIN">
                <input
                  type="text"
                  value={vehicleVin}
                  onChange={(e) => setVehicleVin(e.target.value)}
                  className={inputClass}
                  placeholder="Optional"
                  disabled={!customerExists}
                />
              </Field>
            </div>
            <SaveButton
              onClick={handleSaveVehicle}
              disabled={!customerExists || !hasVehicleInput}
              saving={vehicleSaving}
            />
            <SectionStatus error={vehicleError} success={vehicleSuccess} />
          </div>

          <div className="border-t border-gray-100" />

          {/* Service */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-700">
              <Wrench size={13} className="text-gray-400" />
              Service
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Service">
                <input
                  type="text"
                  value={service}
                  onChange={(e) => setService(e.target.value)}
                  className={inputClass}
                  placeholder="e.g. Oil change"
                />
              </Field>
              <Field label="Opcode">
                <input
                  type="text"
                  value={opcode}
                  onChange={(e) => setOpcode(e.target.value)}
                  className={inputClass}
                  placeholder="e.g. LOF"
                />
              </Field>
            </div>
            <SaveButton onClick={handleSaveService} disabled={!hasServiceInput} saving={serviceSaving} />
            <SectionStatus error={serviceError} success={serviceSuccess} />
          </div>

          <div className="border-t border-gray-100" />

          {/* Transportation */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-700">
              <Truck size={13} className="text-gray-400" />
              Transportation
            </div>
            <Field label="Transport">
              <select
                value={transportationType}
                onChange={(e) => setTransportationType(e.target.value)}
                className={inputClass}
              >
                <option value="">Select...</option>
                <option value="Dropoff">Dropoff</option>
                <option value="Waiter">Waiter</option>
                <option value="Loaner">Loaner</option>
                <option value="Pickup">Pickup</option>
              </select>
            </Field>
            <SaveButton
              onClick={handleSaveTransport}
              disabled={!transportationType.trim()}
              saving={transportSaving}
            />
            <SectionStatus error={transportError} success={transportSuccess} />
          </div>
        </div>

        <div className="flex items-center justify-end px-5 py-3 border-t border-gray-100 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditLeadDetailsModal;
